import type { ReactNode } from 'react';
import {
	DrawnSignatureSource,
	EidEasySigningMethod,
} from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import type { SignatureSuccessDetails } from './steps/SignatureSuccessConfirmation/SignatureSuccessConfirmation.types';

export type { EidEasySigningMethod };

/**
 * Common subset of OTP context needed by both the admin and public signing
 * flows. The admin flow extends this with `basePath` for programme-scoped
 * REST endpoints; the public flow uses the base directly.
 */
export interface OtpContextBase {
	channel: SignerContactType;
	maskedContact: string;
	docId: string;
	clientId: string;
	baseUrl: string;
	mockMode: boolean;
}

/**
 * Result of an OTP "resend code" request. eID Easy enforces its own server-side
 * cooldown between dispatches; when it rejects with a "please wait N seconds"
 * message we surface that as `rate_limited` (NOT an error) so the entry screen
 * can re-arm its countdown to the server's actual remaining time instead of
 * sending the user to the error screen.
 */
export type OtpResendOutcome =
	| { status: 'sent' }
	| { status: 'rate_limited'; waitSeconds: number }
	| { status: 'failed' };

export enum EidEasySignStatus {
	IDLE = 'idle',
	DOCUMENT_REVIEW = 'document-review',
	METHOD_SELECT = 'method-select',
	SETUP_SIGNATURE = 'setup-signature',
	SMART_ID_INPUT = 'smart-id-input',
	FREJA_INPUT = 'freja-input',
	DRAW_SIGNATURE = 'draw-signature',
	OTP_INPUT = 'otp-input',
	OTP_CODE_ENTRY = 'otp-code-entry',
	PREPARING = 'preparing',
	AWAITING_SIGN = 'awaiting-sign',
	COMPLETING = 'completing',
	DONE = 'done',
	ERROR = 'error',
}

export interface BulkProgress {
	phase: 'preparing' | 'completing';
	current: number;
	total: number;
}

export const COUNTRY_OPTIONS = [
	{ value: 'BE', label: 'Belgium' },
	{ value: 'EE', label: 'Estonia' },
	{ value: 'FI', label: 'Finland' },
	{ value: 'LV', label: 'Latvia' },
	{ value: 'LT', label: 'Lithuania' },
];

/**
 * Lives here rather than in the component file because surfaces that build these props outside the
 * JSX (e.g. the KYC forms' page-level `useKycFormSigning`) need to name the type.
 */
export interface EidEasySigningModalProps {
	status: EidEasySignStatus;
	challengeCode: string | null;
	error: string | null;
	/** The signing method active when `error` was set; shown on the error step. */
	selectedMethod?: EidEasySigningMethod | null;
	isSigningInProgress: boolean;
	isModalOpen: boolean;
	fileId?: string;
	fileName?: string;
	allowedMethods?: EidEasySigningMethod[];
	restrictToCountry?: boolean;
	countryCode?: string | null;
	onClose: () => void;
	/** Fired once the close animation has finished — the safe point to drop per-signing state. */
	onExitTransitionEnd?: () => void;
	onConfirmReview?: () => void;
	onSelectMethod: (method: EidEasySigningMethod) => void;
	onGoBack: () => void;
	onConfirmSmartId?: (idcode: string, countryCode: string) => void;
	onConfirmFreja?: (email: string) => void;
	onConfirmSignature?: (signatureDataUrl: string, source: DrawnSignatureSource) => void;
	onSignatureConfirmed?: (
		signatureBase64: string,
		source: DrawnSignatureSource
	) => void | Promise<void>;
	onConfirmOtp?: (channel: SignerContactType, value: string) => void;
	onSubmitOtpCode?: (code: string) => void;
	onResendOtpCode?: () => Promise<OtpResendOutcome>;
	otpVerifying?: boolean;
	otpCodeError?: string | null;
	otpChannel?: SignerContactType;
	otpMaskedContact?: string;
	defaultPhone?: string;
	defaultPhonePrefix?: string;
	defaultEmail?: string;
	progress?: BulkProgress | null;
	/**
	 * Skip the saved-signature pre-step and the auto-save. Set for witness signing: the actor's
	 * saved profile signature is not the witness's.
	 */
	disableSavedSignature?: boolean;
	/** Success confirmation data; when present the DONE step renders the full confirmation. */
	successDetails?: SignatureSuccessDetails;
	/**
	 * Replaces the eID Easy panel content entirely; the document surface stays visible. Used for
	 * the witness-configuration step and the witness success screen.
	 */
	panelOverride?: ReactNode;
	/**
	 * Optional content rendered above the eID Easy panel content (e.g. a Back
	 * header). Ignored while `panelOverride` is active.
	 */
	panelHeader?: ReactNode;
}

export interface OtpFormValues {
	phoneNumber: string;
	telephonePrefix: string;
	email: string;
}
